import React from 'react';
import Styled from 'styled';
import { Mutation } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';

import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import lock from 'four-nets/icons/svg/icon_lock.svg';
import iconDelete from 'four-nets/icons/svg/icon_delete_2.svg';

import { AlertContext } from 'lib/alert-context/alert-context';
import Member from 'four-nets/photoblog/groups/group/member/member';
import UserInput from 'four-nets/photoblog/groups/users-input/users-input';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';

import removeBlockMutation, {
    RemoveGroupMemberBlockMutation,
    RemoveGroupMemberBlockMutationVariables,
} from './mutations/remove-block.graphql';
import blockMemberMutation, {
    BlockGroupMemberMutation,
    BlockGroupMemberMutationVariables,
} from './mutations/block-member.graphql';

import { BlockingSettingsFragment } from './blocking-settings.graphql';
import defaultStyles from './blocking-settings.scss';

export interface Props extends BlockingSettingsFragment {
    styles: typeof defaultStyles;
}

interface State {
    username?: string;
    reason?: string;
    value: string;
    key: number;
}

class BlockingSettings extends React.PureComponent<Props, State> {
    state: State = { value: '', key: 0 };
    ref: React.RefObject<HTMLDivElement> = React.createRef();

    onChange = (users: string[], value: string) => {
        this.setState({
            username: (users && users.length > 0 && users[0]) || undefined,
            value,
        });
    };

    onReasonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ reason: event.target.value });
    };

    render() {
        const { styles, id, blockedMembers } = this.props;
        const { value, username, reason } = this.state;

        return (
            <AlertContext.Consumer>
                {({ confirm }) => (
                    <div className={styles.form}>
                        <FormattedMessage id='BLOCKED_MEMBERS_LIST'>
                            {message => <div className={styles.label}>{message}</div>}
                        </FormattedMessage>
                        <ul className={styles.blockedUsers}>
                            {blockedMembers &&
                                blockedMembers.edges.map(edge =>
                                    edge && edge.node ? (
                                        <Mutation<
                                            RemoveGroupMemberBlockMutation,
                                            RemoveGroupMemberBlockMutationVariables
                                        >
                                            mutation={removeBlockMutation}
                                        >
                                            {removeBlock => (
                                                <Member {...edge.node!}>
                                                    <button
                                                        onClick={() => {
                                                            const onAccept = () => {
                                                                return removeBlock({
                                                                    variables: {
                                                                        input: { groupMemberId: edge.node!.id },
                                                                    },
                                                                });
                                                            };

                                                            confirm({
                                                                acceptButtonLabel: (
                                                                    <FormattedMessage id='BLOCKED_MEMBER_CANCEL_BLOCKING' />
                                                                ),
                                                                body: (
                                                                    <React.Fragment>
                                                                        <FormattedMessage id='ARE_YOU_SURE_VENDOR' />
                                                                    </React.Fragment>
                                                                ),
                                                                onAccept,
                                                            });
                                                        }}
                                                        className={styles.closeButton}
                                                    >
                                                        <SVGIcon src={iconDelete} className={styles.timesIcon} />
                                                    </button>
                                                </Member>
                                            )}
                                        </Mutation>
                                    ) : null
                                )}
                        </ul>
                        {!blockedMembers || !blockedMembers.edges || blockedMembers.edges.length === 0 ? (
                            <FormattedMessage id='NO_BLOCKED_MEMBERS' />
                        ) : null}
                        <FormattedMessage id='BLOCK_MEMBER_USERNAME'>
                            {message => <div className={styles.label}>{message}</div>}
                        </FormattedMessage>
                        <UserInput groupId={id} key={this.state.key} onChange={this.onChange} maxLength={1} />
                        <FormattedMessage id='BLOCK_MEMBER_REASON'>
                            {message => <div className={styles.label}>{message}</div>}
                        </FormattedMessage>
                        <TextArea onChange={this.onReasonChange} value={reason || ''} />
                        <FormattedMessage id='ADMIN_WILL_BE_INFORMED'>
                            {msg => <span className={styles.description}>{msg}</span>}
                        </FormattedMessage>
                        <Mutation<BlockGroupMemberMutation, BlockGroupMemberMutationVariables>
                            mutation={blockMemberMutation}
                        >
                            {blockMember => (
                                <button
                                    className={styles.submitButton}
                                    onClick={() => {
                                        if ((username || value) && reason) {
                                            blockMember({
                                                variables: {
                                                    input: {
                                                        groupId: id,
                                                        username: username || value,
                                                        reason,
                                                    },
                                                },
                                            })
                                                .catch((err: Error) => {
                                                    confirm({
                                                        acceptButtonLabel: <FormattedMessage id='BUTTON_LABEL_OK' />,
                                                        body: err.message,
                                                    });
                                                })
                                                .then(() =>
                                                    this.setState(prevState => ({
                                                        key: prevState.key + 1,
                                                        reason: undefined,
                                                    }))
                                                );
                                        }
                                    }}
                                    disabled={!((username || value) && reason)}
                                >
                                    <SVGIcon src={lock} className={styles.submitIcon} />
                                    <FormattedMessage id='BLOCK_MEMBER_SUBMIT' />
                                </button>
                            )}
                        </Mutation>
                    </div>
                )}
            </AlertContext.Consumer>
        );
    }
}

export default Styled(defaultStyles)(BlockingSettings);
